import { css } from '@emotion/css'
import Backdrop from '@material-ui/core/Backdrop'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Paper from '@material-ui/core/Paper'
import { lighten, makeStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import GetAppIcon from '@material-ui/icons/GetApp'
import clsx from 'clsx'
import { cloneDeep, orderBy } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import PurchaseTableCSV from 'src/components/ui/Csv/PurchaseTableCSV'
import AddPurchaseTagsModal from 'src/components/ui/Modals/AddPurchaseTagsModal'
import EditPurchaseInvoiceModal from 'src/components/ui/Modals/EditPurchaseInvoiceModal'
import SupplierTransactionTable from 'src/components/ui/Table/SupplierTransactionTable'
import * as queries from 'src/graphql/queries'
import { useFetchUserInfoQuery } from 'src/redux/api/graphql/userInfoApi'
import {
	fetchPurchases,
	fetchPurchasesFreelancer,
	setAddTagsModalOpenStatus,
	setCurrentPurchase,
	setEditInvoiceModalOpenStatus,
} from 'src/redux/slices/purchases'
import {
	fetchCurrencyRates,
	setCompanyInfo,
	setCompanyLogoUrl,
} from 'src/redux/slices/settings'
import { fetchSuppliers } from 'src/redux/slices/suppliers'
import { API, graphqlOperation, Storage } from 'src/utils/amplify/legacy'
import { getPurchaseTotals } from 'src/utils/invoiceUtils/invoiceTotals'

const emailClass = css`
  display: inline-block;
  min-height: 2rem;
  overflow-wrap: break-word;
  letter-spacing: 0.12rem;
  font-size: 1.2rem;
  text-align: left;
  line-height: 1.5;
  padding: 0.2rem 1rem;
  cursor: pointer;
  ${
		'' /* color: #4465c1;
  background-color: rgb(229, 241, 254); */
  }
  color: #ABA8A8;
  background-color: rgba(196, 196, 196, 0.2);
  font-style: italic;
  font-weight: bold;
  font-size: 0.8rem;
  line-height: 1.6rem;
  margin-right: 1rem;
`

function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1
	}
	if (b[orderBy] > a[orderBy]) {
		return 1
	}
	return 0
}

function getComparator(order, orderBy) {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, order, orderByColumn, comparator) {
	const archived = array.filter(
		(el, index) => el.paymentStatus === 'Archived' && el
	)
	const unarchived = array.filter(
		(el, index) => el.paymentStatus !== 'Archived' && el
	)
	const stabilizedThis = unarchived.map((el, index) => [el, index])

	// stabilizedThis.sort((a, b) => {
	//   const order = comparator(a[0], b[0])
	//   if (order !== 0) return order
	//   return a[1] - b[1]
	// })
	const sortItems =
		order === 'desc'
			? // ? orderBy(unarchived, ['invoiceKey', orderByColumn], ['desc', 'desc'])
				orderBy(unarchived, [orderByColumn], ['desc'])
			: // : orderBy(unarchived, ['invoiceKey', orderByColumn], ['asc', 'asc'])
				orderBy(unarchived, [orderByColumn], ['asc'])
	const cloneSortItems = cloneDeep(sortItems)
	const indexedCloneSortItems = cloneSortItems.map((el, idx) => {
		el.index = idx
		return el
	})
	const groupItems = indexedCloneSortItems.map((si) => {
		if (!si.groupType && si.invoiceAvailable) {
			const getGroup = indexedCloneSortItems.filter(
				(items) => items.invoiceKey === si.invoiceKey
			)
			if (getGroup.length > 1) {
				getGroup.map((gG, index) => {
					if (index === 0) {
						gG.groupType = 'primary'
					} else {
						gG.groupType = 'secondary'
						gG.sortIndex = getGroup[0].index
					}
					return gG
				})
			}
		}
		return si
	})
	let spliceItems = cloneDeep(groupItems)
	for (let i = 0; i < groupItems.length; i++) {
		if (groupItems[i].sortIndex) {
			const deletedItem = spliceItems.filter((gi) => gi.index === i)[0]
			spliceItems = spliceItems.filter((gi) => gi.index !== i)
			spliceItems.splice(groupItems[i].sortIndex + 1, 0, deletedItem)
		}
	}

	// const getGroupedItems = groupItems.map(gi => {
	//   if (gi.invoiceAvailable) {
	//     const getGroup = indexedCloneSortItems.filter(items => items.invoiceKey === gi.invoiceKey)
	//     if (getGroup.length > 1) {
	//     }
	//     return gi
	//   }
	// })

	// let sorted = [...stabilizedThis.map(el => el[0]), ...archived]
	const sorted = [...spliceItems, ...archived]

	return sorted
}

const headCells = [
	{ id: 'createdOn', numeric: false, disablePadding: true, label: 'date' },
	{
		id: 'supplierName',
		numeric: false,
		disablePadding: false,
		label: 'supplier',
	},
	{
		id: 'recapCostlineItem',
		numeric: false,
		disablePadding: false,
		label: 'costline',
	},
	{ id: 'quoteName', numeric: false, disablePadding: false, label: 'quote' },
	{
		id: 'projectName',
		numeric: false,
		disablePadding: false,
		label: 'project',
	},
	{
		id: 'supplierType',
		numeric: false,
		disablePadding: false,
		label: 'supplierType',
	},
	{
		id: 'provisionalTotal',
		numeric: true,
		disablePadding: false,
		label: 'budgetAmount',
	},
	{
		id: 'pretaxAmount',
		numeric: true,
		disablePadding: false,
		label: 'pretaxAmount',
	},
	{
		id: 'invoiceTotal',
		numeric: true,
		disablePadding: false,
		label: 'invoiceTotal',
	},
	{
		id: 'poAvailable',
		numeric: false,
		disablePadding: false,
		label: 'poAvailable',
	},
	{ id: 'tags', numeric: false, disablePadding: false, label: 'tags' },
	{
		id: 'paymentStatus',
		numeric: false,
		disablePadding: false,
		label: 'status',
	},
	{ id: 'action', numeric: false, disablePadding: false, label: 'action' },
]

function EnhancedTableHead(props) {
	const { classes, order, orderBy, rowCount, onRequestSort } = props
	const createSortHandler = (property) => (event) => {
		onRequestSort(event, property)

		// orderBy
	}

	const { t: tSupplierTransaction } = useTranslation('supplierTransaction')

	return (
		<TableHead>
			<TableRow>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={
							headCell.numeric
								? 'right'
								: headCell.id !== 'action'
									? 'left'
									: 'center'
						}
						padding="default"
						sortDirection={orderBy === headCell.id ? order : false}
						style={{
							backgroundColor: '#fafafa',
							fontWeight: '600',
							padding: '16px',
						}}
						className={clsx({
							[classes.action]:
								headCell.id === 'action' ||
								headCell.id === 'status',
						})}
					>
						<div
							className={clsx({
								[classes.resizeCells]:
									headCell.id !== 'poAvailable' &&
									headCell.id !== 'invoiceAvailable' &&
									headCell.id !== 'paymentStatus' &&
									headCell.id !== 'action' &&
									headCell.id !== 'tags',
								[classes.date]: headCell.id === 'createdOn',
								[classes.action]: headCell.id === 'action',
								[classes.resizeTagsCell]:
									headCell.id === 'tags',
							})}
							style={{
								display: 'flex',
								whiteSpace: 'nowrap',
								width: headCell.numeric && 'unset',
								marginRight: headCell.id === 'action' && '1rem',
								marginLeft:
									headCell.id === 'createdOn' && '0.5rem',
								justifyContent:
									(headCell.id === 'action' ||
										headCell.id === 'poAvailable' ||
										headCell.id === 'invoiceAvailable' ||
										headCell.id === 'paymentStatus') &&
									'center',
							}}
						>
							<TableSortLabel
								active={orderBy === headCell.id}
								direction={
									orderBy === headCell.id ? order : 'desc'
								}
								onClick={createSortHandler(headCell.id)}
								hideSortIcon={headCell.id !== 'supplierName'}
							>
								<div
									className={clsx({
										[classes.rowReverse]:
											headCell.id ===
												'provisionalTotal' ||
											headCell.id === 'invoiceTotal' ||
											headCell.id === 'pretaxAmount',
									})}
								>
									{tSupplierTransaction(
										`headers.${headCell.label}`
									)}
								</div>
							</TableSortLabel>
						</div>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	)
}

EnhancedTableHead.propTypes = {
	classes: PropTypes.object.isRequired,
	onRequestSort: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
	rowCount: PropTypes.number.isRequired,
}

const useToolbarStyles = makeStyles((theme) => ({
	root: {
		paddingLeft: theme.spacing(2),
		marginRight: '1rem',
		paddingRight: 0,
		width: '100%',
		fontSize: '0.85rem',
	},
	root2: {
		paddingLeft: theme.spacing(2),
		marginRight: '1rem',
		paddingRight: 0,
		width: '100%',
		justifyContent: 'right',
	},
	highlight:
		theme.palette.type === 'light'
			? {
					color: theme.palette.secondary.main,
					backgroundColor: lighten(
						theme.palette.secondary.light,
						0.85
					),
				}
			: {
					color: theme.palette.text.primary,
					backgroundColor: theme.palette.secondary.dark,
				},
	title: {
		flex: '1 1 100%',
		fontWeight: '600',
	},
	button: {
		margin: '0 0.25rem',
		whiteSpace: 'nowrap',
		fontSize: '0.8rem',
	},

	'@media (max-width: 959px)': {
		root: {
			textAlign: 'center',
		},
		root2: {
			justifyContent: 'center',
		},
	},
}))

const EnhancedTableToolbar = (props) => {
	const {
		filteredRows,
		suppliersData,
		supplierId,
		stableSort,
		order,
		orderBy,
	} = props
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const classes = useToolbarStyles()
	const { t: tSupplierTransaction } = useTranslation('supplierTransaction')

	const [anchorEl, setAnchorEl] = useState(null)

	const handleClose = () => {
		setAnchorEl(null)
	}

	const getFilteredRows = () => {
		return filteredRows
	}

	return (
		<>
			<Grid
				container
				spacing={0}
				style={{ width: '98%', margin: 'auto' }}
			>
				<Grid item xs={12} md={6}>
					<Toolbar className={clsx(classes.root)}>
						<Typography
							className={classes.title}
							variant="h6"
							id="tableTitle"
							component="div"
						>
							{`${tSupplierTransaction('supplier')} `}
							{
								suppliersData?.filter(
									(data) => data.id === supplierId
								)[0]?.name
							}
						</Typography>
					</Toolbar>
				</Grid>
				<Grid item xs={12} md={6}>
					<Toolbar className={clsx(classes.root2)}>
						<Button
							variant="contained"
							color="primary"
							className={classes.button}
							startIcon={<GetAppIcon />}
							onClick={(e) => setAnchorEl(e.currentTarget)}
						>
							{tSupplierTransaction('export')}
						</Button>
						<Button
							variant="contained"
							color="primary"
							className={classes.button}
							startIcon={<ArrowBackIcon />}
							onClick={() => navigate('/suppliers')}
						>
							{tSupplierTransaction('back')}
						</Button>
					</Toolbar>
				</Grid>
			</Grid>

			<Menu
				id="simple-menu"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleClose}
			>
				<MenuItem
					onClick={() => {
						handleClose()
					}}
				>
					<PurchaseTableCSV
						getFilteredRows={getFilteredRows}
						stableSort={stableSort}
						order={order}
						orderBy={orderBy}
					/>
				</MenuItem>
			</Menu>
		</>
	)
}

const useStyles = makeStyles((theme) => ({
	root: {
		width: '90%',
		// width: '98%',
		margin: 'auto',
		boxShadow: '0px 2px 4px 0px #0000003D',
		'& .MuiTableCell-sizeSmall': {
			paddingRight: '0',
			paddingLeft: '0.5rem',
		},
	},
	paper: {
		width: '100%',
		marginBottom: theme.spacing(2),
	},
	table: {
		minWidth: 750,
	},
	visuallyHidden: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1,
	},
	tableBody: {
		margin: '1rem',
		background: 'white',
		borderRadius: '10px',
		width: 'unset',
		border: '1px solid #C2C9D1',
		maxHeight: '65vh',
		marginBottom: 0,
	},
	downloadLink: {
		color: 'blue',
		textDecoration: 'underline',
		'& p': {
			margin: 0,
			width: 'fit-content',
			'&:hover': {
				cursor: 'pointer',
			},
		},
	},
	status: {
		'& p': {
			margin: 0,
			border: '1px solid black',
			width: 'fit-content',
			padding: '0.25rem 0.5rem',
			borderRadius: '4px',
		},
	},
	statusPaid: { '& p': { borderColor: '#3CC13B', color: '#3CC13B' } },
	statusPending: { '& p': { borderColor: '#F3BB1C', color: '#F3BB1C' } },
	statusOverdue: { '& p': { borderColor: '#F03738', color: '#F03738' } },
	statusPaidLate: { '& p': { borderColor: '#006600', color: '#006600' } },
	cellIcons: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	isArchived: {
		backgroundColor: 'rgba(211,211,211, 0.5)',
		color: 'rgba(211,211,211, 0.5)',
		'& td,th': {
			color: 'gray',
		},
		'& th': {
			color: 'gray',
			backgroundColor: 'transparent',
		},
		'& p': {
			margin: 0,
			width: 'fit-content',
		},
	},
	emptyText: {
		height: '50vh',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		'& p': {
			marginBottom: '0rem',
		},
	},
	loadingIcon: {
		height: '50vh',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		'& p': {
			marginBottom: '0rem',
		},
	},
	menuItems: {
		paddingTop: '0',
		paddingBottom: '0',
	},
	filterMenuButton: {
		width: '100%',
		justifyContent: 'flex-end',
	},
	rootFilterList: {
		width: '100%',
		maxWidth: 360,
		backgroundColor: theme.palette.background.paper,
	},
	nested: {
		paddingLeft: theme.spacing(4),
	},
	resizeCells: {
		resize: 'horizontal',
		overflow: 'hidden',
		width: '7.5rem',
		minWidth: '3.5rem',
		'& div:nth-child(2)': {
			width: '85%',
		},
	},
	resizeTagsCell: {
		resize: 'horizontal',
		overflow: 'hidden',
		width: '3rem',
		minWidth: '3rem',
		'& div:nth-child(2)': {
			width: '85%',
		},
	},
	date: {
		width: '5rem',
	},
	action: {
		width: '6rem',
	},
	availableIcons: {
		width: '2.5rem',
		paddingLeft: '1rem',
	},
	rowReverse: {
		flexDirection: 'row-reverse',
		width: '100%',
	},
	filterIcon: {
		cursor: 'pointer',
		margin: '0 0.5rem',
	},
	filterGrid: {
		marginTop: '1rem',
		display: 'flex',
		alignItems: 'center',
	},
	filterGrid2: {
		marginTop: '1rem',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'end',
	},
	totalChips: {
		marginRight: '1rem',
	},
	filterButton: {
		fontSize: '0.8rem',
		marginLeft: '0.5rem',
		'&:hover': {
			backgroundColor: 'transparent',
		},
	},
	datePicker: {
		'& input': {
			fontSize: '0.8rem',
			padding: '0.5rem',
		},
		'& label': {
			fontSize: '0.8rem',
		},
	},
	formControl: {
		'& div': {
			height: '1.2rem',
		},
	},
	filterInputs: {
		'& input': {
			padding: '0.2rem 0',
			height: '1rem',
		},
	},
	smallMenuItems: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	'@media (min-height: 800px)': {
		tableBody: {
			minHeight: '72vh',
		},
	},
	'@media (min-height: 900px)': {
		tableBody: {
			minHeight: '77vh',
		},
	},
	'@media (min-height: 1000px)': {
		tableBody: {
			minHeight: '82vh',
		},
	},
}))

export default function Purchases() {
	const classes = useStyles()
	const dispatch = useDispatch()
	const { t: tSupplierTransaction } = useTranslation('supplierTransaction')
	const { supplierId } = useParams()
	const [order, setOrder] = useState('desc')
	const [orderBy, setOrderBy] = useState('createdOn')
	const [show, setShow] = useState(false)
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [invoiceId, setInvoiceId] = useState('')
	const [supplierName, setSupplierName] = useState('')
	const [filteredRows, setFilteredRows] = useState([])
	const [filteredPaymentStatus, setFilteredPaymentStatus] = useState({
		Draft: true,
		Pending: true,
		Paid: true,
		Overdue: true,
		Archived: true,
	})
	const [filteredPOStatus, setFilteredPOStatus] = useState({
		Available: true,
		NotAvailable: true,
	})
	const [filteredInvoiceStatus, setFilteredInvoiceStatus] = useState({
		Available: true,
		NotAvailable: true,
	})
	const startDateStorage = JSON.parse(
		window.localStorage.getItem('purchaseStartDate') || 'null'
	)

	const endDateStorage = JSON.parse(
		window.localStorage.getItem('purchaseEndDate') || 'null'
	)

	const [anchorEl, setAnchorEl] = useState(null)
	const [openPaymentStatusFilter, setOpenPaymentStatusFilter] =
		useState(false)
	const [openPOStatusFilter, setOpenPOStatusFilter] = useState(false)
	const [openInvoiceStatusFilter, setOpenInvoiceStatusFilter] =
		useState(false)
	const [totalsObject, setTotalsObject] = useState({})
	const [filteredPurchasesData, setFilteredPurchasesData] = useState([])

	const purchasesData = useSelector((state) => state.purchases.purchasesData)
	const suppliersData = useSelector((state) => state.suppliers.suppliersData)
	const fetchPurchasesApiStatus = useSelector(
		(state) => state.purchases.fetchPurchasesApiStatus
	)
	const currentPurchaseInfo = useSelector(
		(state) => state.purchases.currentPurchaseInfo
	)
	const editInvoiceModalOpenStatus = useSelector(
		(state) => state.purchases.editInvoiceModalOpenStatus
	)
	const userLoginStatus = useSelector((state) => state.auth.userLoginStatus)
	const userInfoId = useSelector((state) => state.auth.userInfo?.id)
	const { data: userInfo } = useFetchUserInfoQuery(
		{
			userInfoId: userInfoId,
		},
		{
			skip: !userInfoId,
		}
	)
	const companyInfo = useSelector((state) => state.settings.companyInfo)
	const currencyRates = useSelector((state) => state.settings.currencyRates)

	const isFreelancer = userInfo?.cognitoGroup?.includes('freelancer')

	const { data: freelancerInfo } = useFetchUserInfoQuery(
		{
			userInfoId: userInfo?.id,
		},
		{
			skip: !isFreelancer || !userLoginStatus || !userInfo?.id,
		}
	)

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget)
	}

	const handleListPaymentClick = () => {
		setOpenPaymentStatusFilter(!openPaymentStatusFilter)
	}

	const handleListPOClick = () => {
		setOpenPOStatusFilter(!openPOStatusFilter)
	}

	const handleListInvClick = () => {
		setOpenInvoiceStatusFilter(!openInvoiceStatusFilter)
	}

	const handleClose = () => {
		setAnchorEl(null)
		setOpenPaymentStatusFilter(false)
		setOpenPOStatusFilter(false)
		setOpenInvoiceStatusFilter(false)
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage)
	}

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(Number.parseInt(event.target.value, 10))
		setPage(0)
	}

	useEffect(() => {
		if (purchasesData?.length > 0) {
			const supplierPurchases = purchasesData?.filter(
				(data) => data.supplierId === supplierId
			)
			setFilteredPurchasesData(supplierPurchases)
			setFilteredRows(supplierPurchases)
		} else {
			setFilteredPurchasesData([])
		}
	}, [purchasesData])

	// biome-ignore lint: Fetching supplier and purchase data when user info or freelancer status changes
	useEffect(() => {
		if (userInfo?.cognitoGroup && !isFreelancer) {
			dispatch(fetchPurchases({}))
			dispatch(fetchSuppliers())
			dispatch(fetchCurrencyRates())
		} else if (userInfo?.cognitoGroup && isFreelancer && freelancerInfo) {
			dispatch(
				fetchPurchasesFreelancer({
					freelancerTenantId: userInfo?.tenantId,
				})
			)
			dispatch(fetchSuppliers())
			dispatch(fetchCurrencyRates())
		}
	}, [userInfo, isFreelancer, dispatch, freelancerInfo])

	// biome-ignore lint: Fetching company details and logo URL on mount - initializes company context for supplier transaction management
	useEffect(() => {
		const getCompanyDetails = async () => {
			const {
				data: { getCompany: companyInfo },
			} = await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userInfo?.currentCompanyTenantId,
				})
			)
			let signedUrl = null
			if (companyInfo?.logoImage?.length > 0) {
				// If key exists only then attempt to get the logo url else set that as null
				signedUrl = await Storage.get(companyInfo?.logoImage[0]?.key, {
					level: 'protected',
					identityId: companyInfo?.logoImage[0]?.identityId,
					expires: 60 * 60 * 12,
				})
			}
			dispatch(setCompanyLogoUrl(signedUrl))
			dispatch(setCompanyInfo(companyInfo ? companyInfo : {}))
		}
		if (userInfo?.currentCompanyTenantId) getCompanyDetails()
	}, [userInfo, dispatch, isFreelancer])

	// biome-ignore lint: Calculating supplier transaction totals in base currency when filtered data or currency rates change
	useEffect(() => {
		if (
			purchasesData.length > 0 &&
			stableSort(filteredRows, order, orderBy).length > 0 &&
			currencyRates &&
			Object.keys(currencyRates).length !== 0 &&
			companyInfo &&
			Object.keys(companyInfo).length !== 0
		) {
			setTotalsObject(
				getPurchaseTotals(
					stableSort(filteredRows, order, orderBy).filter(
						(row) => row.groupType !== 'secondary'
					),
					currencyRates,
					companyInfo
				)
			)
		}
	}, [
		companyInfo,
		currencyRates,
		order,
		orderBy,
		purchasesData.length,
		filteredRows,
	])

	const handleRequestSort = (event, property) => {
		if (property === 'supplierName') {
			const isAsc = orderBy === property && order === 'asc'
			setOrder(isAsc ? 'desc' : 'asc')
			setOrderBy(property)
		}
	}

	const viewInvoices = (row, supplierName) => {
		dispatch(setEditInvoiceModalOpenStatus({ status: true }))
		dispatch(setCurrentPurchase(row))
		setSupplierName(supplierName)
		setInvoiceId(row.id)
	}

	const addTags = (row) => {
		dispatch(setCurrentPurchase(row))
		dispatch(setAddTagsModalOpenStatus({ status: true }))
	}

	const handleStatusFiltering = (event, statusType) => {
		if (statusType === 'payment') {
			setFilteredPaymentStatus({
				...filteredPaymentStatus,
				[event.target.name]: event.target.checked,
			})
		} else if (statusType === 'po') {
			setFilteredPOStatus({
				...filteredPOStatus,
				[event.target.name]: event.target.checked,
			})
		} else {
			setFilteredInvoiceStatus({
				...filteredInvoiceStatus,
				[event.target.name]: event.target.checked,
			})
		}
	}

	const emptyRows =
		rowsPerPage -
		Math.min(rowsPerPage, filteredRows.length - page * rowsPerPage)

	return (
		<div className={classes.root}>
			<Paper className={classes.paper}>
				<EnhancedTableToolbar
					handleClick={handleClick}
					filteredRows={filteredRows}
					stableSort={stableSort}
					order={order}
					orderBy={orderBy}
					suppliersData={suppliersData}
					supplierId={supplierId}
				/>
				<TableContainer className={classes.tableBody}>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						size="small"
						aria-label="enhanced table"
						stickyHeader
					>
						{filteredPurchasesData.length > 0 && (
							<EnhancedTableHead
								classes={classes}
								order={order}
								orderBy={orderBy}
								onRequestSort={handleRequestSort}
								rowCount={filteredPurchasesData.length}
							/>
						)}
						<TableBody>
							{filteredPurchasesData.length > 0 &&
								stableSort(filteredRows, order, orderBy).map(
									(row, index) => {
										const labelId = `enhanced-table-checkbox-${index}`

										return (
											<SupplierTransactionTable
												key={row.id}
												row={row}
												labelId={labelId}
												show={show}
												setShow={setShow}
												companyInfo={companyInfo}
												viewInvoices={viewInvoices}
												addTags={addTags}
											/>
										)
									}
								)}

							{emptyRows > 0 && (
								<TableRow style={{ height: 53 * emptyRows }}>
									{filteredPurchasesData.length === 0 &&
										fetchPurchasesApiStatus ===
											'loading' && (
											<div
												className={classes.loadingIcon}
											>
												<p>
													Loading...
													<CircularProgress />
												</p>
											</div>
										)}
									{filteredPurchasesData.length === 0 &&
										fetchPurchasesApiStatus ===
											'succeeded' && (
											<div className={classes.emptyText}>
												<p>
													{tSupplierTransaction(
														'noPurchases'
													)}
												</p>
											</div>
										)}
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[10, 50, 100]}
					component="div"
					// count={invoicesData.filter(row => filteredPaymentStatus[row.paymentStatus] === true).length}
					count={filteredRows.length}
					// count={purchasesData.length}

					rowsPerPage={rowsPerPage}
					style={{ minHeight: '3rem' }}
					page={page}
					labelRowsPerPage={tSupplierTransaction('rowsPerPage')}
					onChangePage={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
				{editInvoiceModalOpenStatus && (
					<EditPurchaseInvoiceModal
						currentPurchaseId={invoiceId}
						purchasesData={purchasesData}
						suppliersData={suppliersData}
						supplierName={supplierName}
						currentPurchaseInfo={currentPurchaseInfo}
					/>
				)}
				<AddPurchaseTagsModal row={currentPurchaseInfo} />
			</Paper>
			{show && (
				<div>
					<Backdrop
						open={true}
						style={{
							zIndex: 2,
							color: '#fff',
						}}
					>
						{tSupplierTransaction('prepDownload')}
						<CircularProgress color="inherit" />
					</Backdrop>
				</div>
			)}
		</div>
	)
}
